import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Construction from "@mui/icons-material/Construction";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { useTranslation } from 'react-i18next';
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useState, useEffect } from "react";
import { EQUIPMENTS_ENDPOINT, CONTRACT_TERMS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import EquipmentForm from "../../components/ui/equipmentForm/equipmentForm.component";
import JobForm from "../../components/service/jobForm.component";
import { useParams, useNavigate } from "react-router-dom";
import { useCRUD } from "../../api/crud";
import { useSnackbar } from 'notistack';
import axios, { useApiRequest } from "../../api/axios";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useHasAnyPermission, useHasPermission } from "../../contexts/auth";
import EquipmentCounterForm from "../../components/ui/equipmentCounterForm/equipmentCounterForm.component";
import { Equipment } from "../../types/equipments";

export const EquipmentsDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove, get } = useCRUD(EQUIPMENTS_ENDPOINT);
  const {
    loading: archiveLoading,
    error: archiveError,
    executeRequest: executeArchiveRequest,
  } = useApiRequest();
  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const [archiveOpened, setArchiveOpened] = useState<boolean>(false);
  const [updateEquipmentOpened, setUpdateEquipmentOpened] = useState<boolean>(false);
  const [deleteEquipmentOpened, setDeleteEquipmentOpened] = useState<boolean>(false);
  const [addCounterOpened, setAddCounterOpened] = useState<boolean>(false);
  const [createJobOpened, setCreateJobOpened] = useState<boolean>(false);
  const [equipmentClientId, setEquipmentClientId] = useState<string | number | null>(null);
  const canChangeEquipment = useHasPermission("settlement.change_equipment");
  const canDeleteEquipment = useHasPermission("settlement.delete_equipment");
  // Archiving moves the machine in or out of the plan's device limit, so it
  // has its own permission - held by the admin groups only.
  const canArchiveEquipment = useHasPermission("settlement.archive_equipment");
  const canAddCounter = useHasPermission("settlement.add_equipmentcounter");
  const canAddJob = useHasAnyPermission(["service.add_job", "service.add_internal_job"]);

  useEffect(() => {
    const fetchEquipment = async () => {
      if (!id) return;
      const response = await get(id);
      if (response?.data) {
        setEquipment(response.data);
      }
    };

    fetchEquipment();
    window.addEventListener("equipmentUpdated", fetchEquipment);
    return () => {
      window.removeEventListener("equipmentUpdated", fetchEquipment);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    axios.get(`${CONTRACT_TERMS_ENDPOINT}?equipment=${id}`).then(({ data }) => {
      const results = Array.isArray(data?.results) ? data.results : data;
      const firstTerm = Array.isArray(results) ? results[0] : null;
      const clientId = firstTerm?.contract?.client_id ?? null;
      setEquipmentClientId(clientId);
    }).catch(() => {});
  }, [id]);

  const handleDeleteEquipment = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Equipment deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteEquipmentOpened(false);
      navigate('/equipment-section/equipments');
    } else {
      enqueueSnackbar(t('Failed to delete equipment'), ERROR_SNACKBAR);
    }
  };

  const isArchived = Boolean(equipment?.is_archived);

  const handleToggleArchived = async () => {
    if (archiveLoading || !id) {
      return;
    }

    const result = await executeArchiveRequest(() =>
      axios.post(`${EQUIPMENTS_ENDPOINT}${id}/${isArchived ? "restore" : "archive"}/`)
    );

    if (result?.success) {
      enqueueSnackbar(
        isArchived ? t("Equipment restored successfully") : t("Equipment archived successfully"),
        SUCCESS_SNACKBAR
      );
      setArchiveOpened(false);
      setEquipment(result.data);
      window.dispatchEvent(new Event("equipmentUpdated"));
    } else {
      enqueueSnackbar(
        isArchived ? t("Failed to restore equipment") : t("Failed to archive equipment"),
        ERROR_SNACKBAR
      );
    }
  };

  const handleCounterAdded = () => {
    setAddCounterOpened(false);
    window.dispatchEvent(new Event("equipmentCounterAdded"));
  };

  const handleJobCreated = () => {
    setCreateJobOpened(false);
    window.dispatchEvent(new Event("equipmentJobAdded"));
    navigate("/service/jobs");
  };

  const actions: Action[] = [
    {
      id: "new-job",
      icon: <Construction />,
      label: t("New job"),
      onClick: () => {
        setCreateJobOpened(true);
      },
      // Archived equipment takes no new jobs or readings (the backend
      // refuses them) - it has to be restored first.
      disabled: !canAddJob || isArchived,
    },
    {
      id: "add-new-counter",
      icon: <AddIcon />,
      label: t("Add new counter"),
      onClick: () => {
        setAddCounterOpened(true);
      },
      disabled: !canAddCounter || isArchived,
    },
    {
      icon: <EditIcon />,
      label: t("Update equipment"),
      onClick: () => {
        setUpdateEquipmentOpened(true);
      },
      disabled: !canChangeEquipment,
    },
    {
      id: "archive-equipment",
      icon: isArchived ? <UnarchiveIcon /> : <ArchiveIcon />,
      label: isArchived ? t("Restore equipment") : t("Archive equipment"),
      onClick: () => {
        setArchiveOpened(true);
      },
      disabled: !canArchiveEquipment || !equipment,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete equipment"),
      onClick: () => {
        setDeleteEquipmentOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteEquipment,
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const archiveErrorMessage =
    typeof archiveError === 'string'
      ? archiveError
      : archiveError?.message ?? archiveError?.detail ?? '';

  const archiveModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {isArchived
          ? t("Restoring this equipment will bring it back into service - it can be picked for contracts and jobs again.")
          : t("Archiving this equipment will take it out of service - it is no longer offered when picking equipment for contracts and jobs. Its history is kept and you can restore it at any time.")}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {isArchived
            ? t("Restored equipment counts towards the device limit for the whole current month.")
            : t("Equipment counts towards the device limit for every month it was active in, even briefly - archiving lowers the count from next month. Archived equipment cannot get new jobs or counter readings, and equipment with unfinished jobs cannot be archived.")}
        </Typography>
      </Alert>

      {archiveErrorMessage && <Alert severity="error">{archiveErrorMessage}</Alert>}

      <Button
        variant="contained"
        color={isArchived ? "primary" : "warning"}
        onClick={handleToggleArchived}
        disabled={archiveLoading}
      >
        {archiveLoading
          ? t('Processing...')
          : isArchived
            ? t('Confirm restoring')
            : t('Confirm archiving')}
      </Button>
    </Box>
  );

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this equipment will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteEquipment}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateEquipmentOpened}
        setOpened={setUpdateEquipmentOpened}
        title={t("Update equipment")}
        content={
          <EquipmentForm
            id={id}
            onSuccess={() => {
              setUpdateEquipmentOpened(false);
              window.dispatchEvent(new Event("equipmentUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={archiveOpened}
        setOpened={setArchiveOpened}
        title={isArchived ? t("Restore equipment") : t("Archive equipment")}
        content={archiveModalContent}
      />
      <CustomModal
        opened={deleteEquipmentOpened}
        setOpened={setDeleteEquipmentOpened}
        title={t("Delete equipment")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={addCounterOpened}
        setOpened={setAddCounterOpened}
        title={t("Add new counter")}
        content={
          <EquipmentCounterForm
            onSuccess={handleCounterAdded}
            equipmentId={id}
            hiddenFields={["equipment_id", "job"]}
          />
        }
      />
      <CustomModal
        opened={createJobOpened}
        setOpened={setCreateJobOpened}
        title={t("New job")}
        content={
          <JobForm
            onSuccess={handleJobCreated}
            initialClientId={equipmentClientId}
            initialEquipmentId={id}
          />
        }
      />
    </>
  );
};
